<template>
  <v-dialog :model-value="visible" max-width="850" persistent>
    <v-card class="pa-5">
      <v-card-title class="px-0 pt-0">
        Audit de la migration CI98
      </v-card-title>

      <v-card-text class="px-0">
        <div v-if="!synchro98.synchroCompleted && rows.length === 0" class="d-flex align-center ga-3 py-4">
          <v-progress-circular indeterminate color="primary" size="24" />
          <span>Démarrage de l’audit…</span>
        </div>
        <table v-else-if="rows.length > 0" class="resynch-progress-table resynch-steps-table">
          <thead>
            <tr>
              <th>Étape</th>
              <th>Avancement</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td>{{ row.label }}</td>
              <td>{{ row.detail }}</td>
            </tr>
          </tbody>
        </table>

        <section v-if="synchro98.synchroCompleted && audit" class="resynch-audit-summary mt-6">
          <template v-if="audit.isCompliant">
            <h3>Migration conforme</h3>
            <div>Salariés importés : {{ audit.salariesImported }}</div>
            <div>Contrats &amp; paies importés : {{ audit.contratsPaiesImported }}</div>
            <div>Documents importés : {{ audit.documentsImported }}</div>
            <div v-if="audit.logoImported">Logo importé</div>
          </template>

          <template v-else>
            <h3>Synthèse des actions proposées</h3>

            <h4>Synchroniser contrats, paies et salariés</h4>
            <p>
              {{ audit.contratsToSynchronize }} contrat(s),
              {{ audit.paiesToSynchronize }} paie(s) et
              {{ audit.salariesToSynchronize }} salarié(s) à synchroniser.
              Paies : {{ audit.paiesFinalized }} finalisée(s),
              {{ audit.paiesToCart }} à régler.
            </p>
            <table v-if="audit.rows.length > 0" class="resynch-progress-table">
              <thead>
                <tr>
                  <th>Code chèque</th>
                  <th>Code contrat</th>
                  <th>Matricule</th>
                  <th>État de la paie</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in audit.rows" :key="row.codeContrat">
                  <td>{{ row.codeCheque }}</td>
                  <td>{{ row.codeContrat }}</td>
                  <td>{{ row.matricule }}<span v-if="row.salarieToSynchronize"> (*)</span></td>
                  <td>{{ paieStateLabel(row) }}</td>
                </tr>
              </tbody>
            </table>
            <p v-else>Aucun contrat, aucune paie et aucun salarié à synchroniser.</p>
            <p v-if="audit.salariesToSynchronize > 0" class="audit-legend">
              (*) salarié absent localement, à synchroniser.
            </p>

            <h4>Synchroniser les documents</h4>
            <p>
              {{ audit.documentsToRetrieve }} document(s) à récupérer.
            </p>
          </template>
        </section>
      </v-card-text>

      <v-card-actions v-if="synchro98.synchroCompleted" class="justify-end px-0 pb-0 ga-2">
        <v-btn
            v-if="audit && (audit.contratsToSynchronize > 0 || audit.paiesToSynchronize > 0 || audit.salariesToSynchronize > 0)"
            color="primary"
            variant="outlined"
            :disabled="busy"
            @click="synchronizeData">
          Synchroniser contrats, paies et salariés
        </v-btn>
        <v-btn
            v-if="audit && audit.documentsToRetrieve > 0"
            color="primary"
            variant="outlined"
            :disabled="busy"
            @click="synchronizeDocuments">
          Synchroniser les documents
        </v-btn>
        <v-btn color="primary" :disabled="busy" @click="$emit('close')">Fermer</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import Synchro98, { type ResynchAuditRow } from '@/extend/classes/Synchro98';
import { ResynchCommandActions } from '@/generated/classes/Synchro98';

interface ResynchProgressRow {
  id: string;
  label: string;
  detail: string;
}

export default defineComponent({
  name: 'ResynchProgressDialog',
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    synchro98: {
      type: Object as PropType<Synchro98>,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'execute'],
  computed: {
    audit() {
      return this.synchro98.resynchAudit || null;
    },
    rows(): ResynchProgressRow[] {
      const progresses = this.synchro98.progresses || [];
      return progresses.map((progress, index) => {
        const isLast = index === progresses.length - 1;
        const isTransfer = progress.label.startsWith('Transfert CI98');
        const completed = !isLast ||
          (progress.total > 0 && progress.current >= progress.total) ||
          (progress.current === -1 && progress.total === -1) ||
          (!isTransfer && isLast && this.synchro98.synchroCompleted && progress.total === 0);
        const interrupted = isLast && this.synchro98.synchroCompleted && !completed;

        let detail: string;
        if (isTransfer) {
          const current = Math.max(0, progress.current);
          detail = `avancement_transfert : ${current} — seuil attendu : ${progress.total}`;
          if (completed) {
            detail += ' — terminé';
          } else if (interrupted) {
            detail += ' — interrompu';
          }
        } else if (progress.current === -1 && progress.total === -1) {
          detail = 'Terminé';
        } else if (progress.total > 0) {
          detail = `${progress.current} / ${progress.total} ${this.processedLabel(progress.label)}`;
          if (completed) {
            detail += ' — terminé';
          } else if (interrupted) {
            detail += ' — interrompu';
          }
        } else if (completed) {
          detail = `0 ${this.processedLabel(progress.label)} — terminé`;
        } else {
          detail = 'Lecture des données…';
        }

        return {
          id: `${index}-${progress.label}`,
          label: progress.label,
          detail,
        };
      });
    },
  },
  methods: {
    synchronizeData(): void {
      this.$emit('execute', ResynchCommandActions.SYNCHRONIZE_DATA);
    },
    synchronizeDocuments(): void {
      this.$emit('execute', ResynchCommandActions.SYNCHRONIZE_DOCUMENTS);
    },
    paieStateLabel(row: ResynchAuditRow): string {
      if (row.paieStatut === 6) {
        return 'À régler';
      }
      if (row.paieStatut === 8 || row.paieStatut === 11) {
        return 'Finalisée';
      }
      return 'Brouillon';
    },
    processedLabel(label: string): string {
      return label === 'Paies' ? 'traitées' : 'traités';
    },
  },
});
</script>

<style scoped>
.resynch-progress-table {
  width: 100%;
  border-collapse: collapse;
  font-family: monospace;
  font-size: 13px;
}

.resynch-progress-table th,
.resynch-progress-table td {
  padding: 8px 10px;
  border-bottom: 1px solid rgb(var(--v-theme-grey90));
  text-align: left;
  vertical-align: top;
}

.resynch-progress-table th:first-child,
.resynch-progress-table td:first-child {
  width: 38%;
  font-weight: 600;
}

.resynch-steps-table {
  table-layout: fixed;
}

.resynch-steps-table th:first-child,
.resynch-steps-table td:first-child {
  width: 72%;
}

.resynch-steps-table th:last-child,
.resynch-steps-table td:last-child {
  width: 28%;
}

.resynch-audit-summary h3,
.resynch-audit-summary h4 {
  margin: 16px 0 8px;
}

.audit-legend {
  margin-top: 8px;
  font-size: 12px;
}
</style>
